// @flow

import { observer } from "mobx-react";
import React from "react";
import ToggleButton from "react-toggle-button";

import GlobalSettingsStore from "app/stores/GlobalSettingsStore";

import Panel from "app/components/Panel/Panel";
import PageTitle from "app/components/PageTitle/PageTitle";

import "./GlobalSettingsView.scss";

type Props = {};

type State = {
    screenshotEngine: string,
    videoCardTheme: string,
    logo: string,
    requireEarlyAccess: boolean,
    cardSwitcherEffect: string,
    newItemFollowEffect: string,
    defaultTheme: string,
    mediaRecordingAudioDurationLimit: number,
    mediaRecordingVideoDurationLimit: number
};

@observer
export default class GlobalSettingsView extends React.Component<Props, State> {
    constructor(opts: any) {
        super(opts);

        this.state = {
            screenshotEngine: GlobalSettingsStore.screenshotEngine,
            videoCardTheme: GlobalSettingsStore.videoCardTheme,
            logo: GlobalSettingsStore.logo,
            requireEarlyAccess: GlobalSettingsStore.requireEarlyAccess,
            cardSwitcherEffect: GlobalSettingsStore.cardSwitcherEffect,
            newItemFollowEffect: GlobalSettingsStore.newItemFollowEffect,
            defaultTheme: GlobalSettingsStore.defaultTheme,
            mediaRecordingAudioDurationLimit:
                GlobalSettingsStore.mediaRecordingAudioDurationLimit,
            mediaRecordingVideoDurationLimit:
                GlobalSettingsStore.mediaRecordingVideoDurationLimit
        };
    }

    handleToggle(param: string, value: any) {
        value = !value;

        this.setState({ [param]: value });
        GlobalSettingsStore.update(param, value ? 1 : 0);
    }

    handleInput(event: SyntheticInputEvent<*>) {
        const target = event.target;
        const param = target.name;
        const value = target.value;

        this.setState({ [param]: value });
        GlobalSettingsStore.update(param, value);
    }

    render() {
        return (
            <div className="GlobalSettingsView">
                <Panel>
                    <PageTitle>
                        <i className="fa fa-paint-brush" /> Visual settings
                    </PageTitle>

                    <div className="GlobalSettingsView__list">
                        {this.renderVideoCardTheme()}
                        {this.renderLogo()}
                        {this.renderCardSwitcherEffect()}
                        {this.renderNewItemFollowEffect()}
                        {this.renderDefaultTheme()}
                    </div>
                </Panel>

                <Panel>
                    <PageTitle>
                        <i className="fa fa-plug" /> Feature Settings
                    </PageTitle>

                    <div className="GlobalSettingsView__list">
                        {this.renderEarlyAccess()}
                        {this.renderMediaRecordingDurationLimits()}
                    </div>
                </Panel>

                <Panel>
                    <PageTitle>
                        <i className="fa fa-cog" /> Engine Settings
                    </PageTitle>

                    <div className="GlobalSettingsView__list">
                        {this.renderScreenshotEngine()}
                    </div>
                </Panel>
            </div>
        );
    }

    renderDefaultTheme() {
        return (
            <div className="GlobalSettingsView__option">
                <h3 className="GlobalSettingsView__option-title">
                    Default theme
                </h3>
                <p>The theme shown if user is unsigned</p>

                <div className="radio">
                    <label>
                        <input
                            type="radio"
                            name="defaultTheme"
                            value="white"
                            checked={this.state.defaultTheme === "white"}
                            onChange={this.handleInput.bind(this)}
                        />
                        White
                    </label>
                </div>
                <div className="radio">
                    <label>
                        <input
                            type="radio"
                            name="defaultTheme"
                            value="dark"
                            checked={this.state.defaultTheme === "dark"}
                            onChange={this.handleInput.bind(this)}
                        />
                        Dark
                    </label>
                </div>
            </div>
        );
    }

    renderScreenshotEngine() {
        return (
            <div className="GlobalSettingsView__option">
                <h3 className="GlobalSettingsView__option-title">
                    Screenshot mechanism
                </h3>
                <p>Sets the engine used when taking screen shots</p>

                <div className="radio">
                    <label>
                        <input
                            type="radio"
                            name="screenshotEngine"
                            value="1"
                            checked={this.state.screenshotEngine === "1"}
                            onChange={this.handleInput.bind(this)}
                        />
                        Thum.io
                    </label>
                </div>
                <div className="radio">
                    <label>
                        <input
                            type="radio"
                            name="screenshotEngine"
                            value="2"
                            checked={this.state.screenshotEngine === "2"}
                            onChange={this.handleInput.bind(this)}
                        />
                        Chrome headless
                    </label>
                </div>
            </div>
        );
    }

    renderVideoCardTheme() {
        return (
            <div className="GlobalSettingsView__option">
                <h3 className="GlobalSettingsView__option-title">
                    Video Card Theme
                </h3>
                <p>Sets the theme for video cards</p>

                <div className="radio">
                    <label>
                        <input
                            type="radio"
                            name="videoCardTheme"
                            value="default"
                            checked={this.state.videoCardTheme === "default"}
                            onChange={this.handleInput.bind(this)}
                        />
                        Default
                    </label>
                </div>
                <div className="radio">
                    <label>
                        <input
                            type="radio"
                            name="videoCardTheme"
                            value="alternative"
                            checked={
                                this.state.videoCardTheme === "alternative"
                            }
                            onChange={this.handleInput.bind(this)}
                        />
                        Alternative
                    </label>
                </div>
            </div>
        );
    }

    renderLogo() {
        return (
            <div className="GlobalSettingsView__option">
                <h3 className="GlobalSettingsView__option-title">Logo</h3>
                <p>Sets the logo used across the platform</p>

                <div className="radio">
                    <label>
                        <input
                            type="radio"
                            name="logo"
                            value="default"
                            checked={this.state.logo === "default"}
                            onChange={this.handleInput.bind(this)}
                        />
                        Default
                    </label>
                </div>
                <div className="radio">
                    <label>
                        <input
                            type="radio"
                            name="logo"
                            value="dot"
                            checked={this.state.logo === "dot"}
                            onChange={this.handleInput.bind(this)}
                        />
                        With dot
                    </label>
                </div>
            </div>
        );
    }

    renderNewItemFollowEffect() {
        return (
            <div className="GlobalSettingsView__option">
                <h3 className="GlobalSettingsView__option-title">
                    Plus sign follow video progress
                </h3>
                <p>Sets behavior for video plus sign on editor</p>

                <div className="radio">
                    <label>
                        <input
                            type="radio"
                            name="newItemFollowEffect"
                            value="default"
                            checked={
                                this.state.newItemFollowEffect === "default"
                            }
                            onChange={this.handleInput.bind(this)}
                        />
                        Default
                    </label>
                </div>

                <div className="radio">
                    <label>
                        <input
                            type="radio"
                            name="newItemFollowEffect"
                            value="always"
                            checked={
                                this.state.newItemFollowEffect === "always"
                            }
                            onChange={this.handleInput.bind(this)}
                        />
                        Always follow progress
                    </label>
                </div>
            </div>
        );
    }

    renderEarlyAccess() {
        return (
            <div className="GlobalSettingsView__option">
                <h3 className="GlobalSettingsView__option-title">
                    Early Access
                </h3>
                <p>Require permission to create videos</p>

                <ToggleButton
                    value={this.state.requireEarlyAccess}
                    onToggle={value =>
                        this.handleToggle("requireEarlyAccess", value)
                    }
                />
            </div>
        );
    }

    renderMediaRecordingDurationLimits() {
        return (
            <div className="GlobalSettingsView__option">
                <h3 className="GlobalSettingsView__option-title">
                    Media recording
                </h3>
                <p>Maximum length of audio and video recordings</p>

                <div className="form-group">
                    <label htmlFor="mediaRecordingAudioDurationLimitInput">
                        Max audio duration (seconds)
                    </label>
                    <input
                        className="form-control"
                        type="number"
                        step="1"
                        name="mediaRecordingAudioDurationLimit"
                        id="mediaRecordingAudioDurationLimitInput"
                        value={this.state.mediaRecordingAudioDurationLimit}
                        onChange={this.handleInput.bind(this)}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="mediaRecordingVideoDurationLimitInput">
                        Max video duration (seconds)
                    </label>
                    <input
                        className="form-control"
                        type="number"
                        step="1"
                        name="mediaRecordingVideoDurationLimit"
                        id="mediaRecordingVideoDurationLimitInput"
                        value={this.state.mediaRecordingVideoDurationLimit}
                        onChange={this.handleInput.bind(this)}
                    />
                </div>
            </div>
        );
    }

    renderCardSwitcherEffect() {
        return (
            <div className="GlobalSettingsView__option">
                <h3 className="GlobalSettingsView__option-title">
                    Card Switcher Effect
                </h3>
                <p>Effect used to display cards on watch page sidebar</p>

                <div className="radio">
                    <label>
                        <input
                            type="radio"
                            name="cardSwitcherEffect"
                            value="slide"
                            checked={this.state.cardSwitcherEffect === "slide"}
                            onChange={this.handleInput.bind(this)}
                        />
                        Slide
                    </label>
                </div>
                <div className="radio">
                    <label>
                        <input
                            type="radio"
                            name="cardSwitcherEffect"
                            value="fade"
                            checked={this.state.cardSwitcherEffect === "fade"}
                            onChange={this.handleInput.bind(this)}
                        />
                        Fade
                    </label>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/views/app/global/Settings/GlobalSettingsView.js